import { Address, encodeFunctionData, getAddress, isAddress, parseAbi, zeroAddress } from "viem";

export type MarketplaceGovernanceActionType = "withdraw" | "set-fees" | "transfer-ownership";

interface MarketplaceGovernanceInput {
  actionType: MarketplaceGovernanceActionType;
  marketplaceAddress?: Address;
  treasuryAddress: Address;
  feeRecipient: string;
  feeBps: string;
  newOwner: string;
}

export interface MarketplaceGovernanceAction {
  target: Address;
  value: bigint;
  calldata: `0x${string}`;
  title: string;
  summary: string;
}

const MARKETPLACE_GOVERNANCE_ABI = parseAbi([
  "function withdrawProceeds()",
  "function setFeeConfiguration(address recipient,uint96 feeBps)",
  "function transferOwnership(address newOwner)",
]);

const requiredAddress = (value: string | undefined, label: string): Address => {
  if (!value || !isAddress(value)) throw new Error(`${label} is not a valid Polygon address.`);
  const address = getAddress(value);
  if (address === zeroAddress) throw new Error(`${label} cannot be the zero address.`);
  return address;
};

const requiredFeeBps = (value: string): bigint => {
  const normalized = value.trim();
  if (!/^\d+$/.test(normalized)) throw new Error("Platform fee must be a whole number of basis points.");
  const feeBps = BigInt(normalized);
  if (feeBps > 1_000n) throw new Error("Platform fee cannot exceed 1,000 bps (10%).");
  return feeBps;
};

export const buildMarketplaceGovernanceAction = (
  input: MarketplaceGovernanceInput,
): MarketplaceGovernanceAction => {
  const target = requiredAddress(input.marketplaceAddress, "Configured marketplace");
  const treasury = requiredAddress(input.treasuryAddress, "DAO treasury");

  if (input.actionType === "withdraw") {
    return {
      target,
      value: 0n,
      calldata: encodeFunctionData({
        abi: MARKETPLACE_GOVERNANCE_ABI,
        functionName: "withdrawProceeds",
      }),
      title: "Withdraw marketplace proceeds",
      summary: `Withdraw all marketplace proceeds credited to DAO treasury ${treasury}. Execution reverts if that address has no proceeds.`,
    };
  }

  if (input.actionType === "set-fees") {
    const feeRecipient = requiredAddress(input.feeRecipient, "Fee recipient");
    const feeBps = requiredFeeBps(input.feeBps);
    return {
      target,
      value: 0n,
      calldata: encodeFunctionData({
        abi: MARKETPLACE_GOVERNANCE_ABI,
        functionName: "setFeeConfiguration",
        args: [feeRecipient, feeBps],
      }),
      title: "Update marketplace fee configuration",
      summary: `Set the marketplace fee recipient to ${feeRecipient} and the platform fee to ${feeBps} bps. This affects future purchases and does not change marketplace ownership.`,
    };
  }

  const newOwner = requiredAddress(input.newOwner, "New marketplace owner");
  if (newOwner.toLowerCase() === treasury.toLowerCase()) {
    throw new Error("The DAO treasury is already the proposed owner. Enter a different new owner.");
  }
  return {
    target,
    value: 0n,
    calldata: encodeFunctionData({
      abi: MARKETPLACE_GOVERNANCE_ABI,
      functionName: "transferOwnership",
      args: [newOwner],
    }),
    title: "Transfer marketplace ownership",
    summary: `Transfer marketplace ownership from DAO treasury ${treasury} to ${newOwner}. After execution, this DAO can no longer administer the marketplace unless ownership is transferred back.`,
  };
};
